import { clearFiltersCreatedByCreator } from '../../../components/Crud/filters/components/DynamicOptionsSelectorsCreator/hooks/useDynamicOptionsSelectorsCreator';
import useDateRangeFilter from '../../../components/Crud/filters/hooks/dateRangeFilter/useDateRangeFilter';
import useMultiDynamicOptionsSelector from '../../../components/Crud/filters/hooks/multiOptionsSelectorFilter/useMultiDynamicOptionsSelector/useMultiDynamicOptionsSelector';
import useMultiStaticOptionsSelector from '../../../components/Crud/filters/hooks/multiOptionsSelectorFilter/useMultiStaticOptionsSelector';
import useRadioFilter from '../../../components/Crud/filters/hooks/radioFilter/useRadioFilter';
import {
  FilterOptionV3,
  MultiDynamicOptionsCreatorFilter,
  PossibleFilters,
} from '../../../components/Crud/filters/sharedBetweenFilters/types';
import useWidth from '../../../components/Hooks/useWidth';
import useDefaultFiltersToList from '../../../hooks/useDefaultFiltersToList';
import useSearchParams from '../../../hooks/useSearchParams';
import LangEnum from '../../../lang/LangEnum';
import { getFindClassificationTypesKey } from '../../../services/modules/classificationTypes/classificationTypes.keys';
import classificationTypesRepository from '../../../services/modules/classificationTypes/classificationTypes.repository';
import {
  getFindCatalogAccessClassificationsFilterOptionsKey,
  getFindCategoriesFilterOptionsKey,
  getFindClassificationsFilterOptionsKey,
  getFindCollectionsFilterOptionsKey,
  getFindColorsFilterOptionsKey,
  getFindCustomersFilterOptionsKey,
  getFindDeliveryWindowsFilterOptionsKey,
  getFindProductsFilterOptionsKey,
  getFindSalesRepresentativesFilterOptionsKey,
} from '../../../services/modules/filterOptions/filterOptions.keys';
import filterOptionsRepository from '../../../services/modules/filterOptions/repositories/filterOptions.repository';
import { FilterPreferenceRecognizedLists } from '../../../services/modules/userContextPreferences/userContextPreferences.types';
import { CLASSIFICATIONS_PREFIX } from '../../classifications/classification-filter.utils';
import { ClassificationTypeEntityEnum } from '../../classifications/classification.types';
import { skuAvailabilityOptions } from '../../products/utils/availability.utils';
import useCanReadSalesRepresentatives from '../../sales-representative/hooks/useCanReadSalesRepresentatives';
import { useTranslation } from '../../translation/TranslationContext';
import useCanManageBrandCatalogs from '../hooks/useCanManageBrandCatalogs';
import { CATALOG_LIST_FILTERS_KEYS } from './catalogListFilter.utils';
import useCatalogListSearchField from './useCatalogListSearchField';

const useCatalogListFilter = () => {
  const { mobileWidth } = useWidth();
  const { translate } = useTranslation();
  const { searchParams } = useSearchParams();

  const canSeeAdminOnlyFilters = useCanManageBrandCatalogs();
  const shouldHideAdminOnlyFilters = !canSeeAdminOnlyFilters;
  const canNotReadSalesRepresentatives = !useCanReadSalesRepresentatives();

  const list = FilterPreferenceRecognizedLists.CATALOGS;
  const repository = filterOptionsRepository();
  const classificationTypeRepository = classificationTypesRepository();

  const { defaultFilters, shouldIgnoreDefaultFilters, defaultFiltersByPrefix } =
    useDefaultFiltersToList({
      list,
      filtersKeys: Object.values(CATALOG_LIST_FILTERS_KEYS),
      filterKeysPrefix: [CLASSIFICATIONS_PREFIX],
    });

  const queryLimit = mobileWidth ? 20 : 10;

  const { searchField, isSearchActive, onClearSearch } = useCatalogListSearchField({
    defaultValue: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.query] as string | undefined,
    shouldIgnoreDefaultFilters,
  });

  const {
    cleanFilter: cleanIsDefaultFilter,
    getSearchParamKeys: getSearchParamKeysOfIsDefaultFilter,
    ...isDefaultFilter
  } = useRadioFilter({
    key: CATALOG_LIST_FILTERS_KEYS.isDefault,
    defaultValue: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.isDefault] as string,
    shouldIgnoreDefaultFilters,
    options: [
      { value: 'true', label: translate(LangEnum.CATALOGS_FILTER_IS_DEFAULT_ALL_PRODUCTS) },
    ],
  });

  const {
    cleanFilter: cleanIsDynamicFilter,
    getSearchParamKeys: getSearchParamKeysOfIsDynamicFilter,
    ...isDynamicFilter
  } = useRadioFilter({
    key: CATALOG_LIST_FILTERS_KEYS.isDynamic,
    defaultValue: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.isDynamic] as string,
    shouldIgnoreDefaultFilters,
    options: [
      { value: 'true', label: translate(LangEnum.CATALOGS_FILTER_IS_DYNAMIC_DYNAMIC) },
      { value: 'false', label: translate(LangEnum.CATALOGS_FILTER_IS_DYNAMIC_MANUAL) },
    ],
  });

  const {
    cleanFilter: cleanIsPublicLinkFilter,
    getSearchParamKeys: getSearchParamKeysOfIsPublicLinkFilter,
    ...isPublicLinkFilter
  } = useRadioFilter({
    key: CATALOG_LIST_FILTERS_KEYS.isPublicLink,
    defaultValue: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.isPublicLink] as string,
    shouldIgnoreDefaultFilters,
    options: [
      { value: 'true', label: translate(LangEnum.CATALOGS_FILTER_IS_PUBLIC_LINK_WITH) },
      { value: 'false', label: translate(LangEnum.CATALOGS_FILTER_IS_PUBLIC_LINK_WITHOUT) },
    ],
  });

  const {
    cleanFilter: cleanDateRangeFilter,
    getSearchParamKeys: getSearchParamKeysOfDateRangeFilter,
    ...dateRangeFilter
  } = useDateRangeFilter({
    initialDateKey: CATALOG_LIST_FILTERS_KEYS.startDate,
    finalDateKey: CATALOG_LIST_FILTERS_KEYS.endDate,
    initialDateLabel: LangEnum.GLOBAL_CREATED_AT,
    finalDateLabel: LangEnum.GLOBAL_FINAL_DATE,
    initialDateDefaultValue: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.startDate] as
      | string
      | undefined,
    finalDateDefaultValue: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.endDate] as
      | string
      | undefined,
    shouldIgnoreDefaultFilters,
  });

  const {
    cleanFilter: cleanCustomerIdsFilter,
    getSearchParamKeys: getSearchParamKeysOfCustomerIdsFilter,
    ...customerIdsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCustomersFilterOptions,
    queryKeyFn: getFindCustomersFilterOptionsKey,
    defaultValues: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.customerIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'customersIds',
    key: CATALOG_LIST_FILTERS_KEYS.customerIds,
    formatLabel: (rawData) => rawData.commercialName,
    defaultQueryParam: {
      limit: queryLimit,
      basedOnList: 'catalogs',
    },
    order: {
      commercialName: 'ASC',
    },
  });

  const {
    cleanFilter: cleanSalesRepresentativeIdsFilter,
    getSearchParamKeys: getSearchParamKeysOfSalesRepresentativeIdsFilter,
    ...salesRepresentativeIdsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findSalesRepresentativesFilterOptions,
    queryKeyFn: getFindSalesRepresentativesFilterOptionsKey,
    defaultValues: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.salesRepresentativeIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'nameOrCode',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_LIST_FILTERS_KEYS.salesRepresentativeIds,
    isDisabled: canNotReadSalesRepresentatives,
    formatLabel: (rawData) => rawData.name,
    defaultQueryParam: {
      limit: queryLimit,
      basedOnList: 'catalogs',
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanClassificationPermissionIdsFilter,
    getSearchParamKeys: getSearchParamKeysOfClassificationPermissionIdsFilter,
    ...classificationPermissionIdsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCatalogAccessClassifications,
    queryKeyFn: getFindCatalogAccessClassificationsFilterOptionsKey,
    defaultValues: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.classificationPermissionIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_LIST_FILTERS_KEYS.classificationPermissionIds,
    formatLabel: (rawData) => rawData.name,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanColorIdsFilter,
    getSearchParamKeys: getSearchParamKeysOfColorIdsFilter,
    ...colorIdsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findColorsFilterOptions,
    queryKeyFn: getFindColorsFilterOptionsKey,
    defaultValues: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.colorIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_LIST_FILTERS_KEYS.colorIds,
    formatLabel: (rawData) => `${rawData.name} (${rawData.code})`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanCollectionIdsFilter,
    getSearchParamKeys: getSearchParamKeysOfCollectionIdsFilter,
    ...collectionIdsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCollectionsFilterOptions,
    queryKeyFn: getFindCollectionsFilterOptionsKey,
    defaultValues: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.collectionIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_LIST_FILTERS_KEYS.collectionIds,
    formatLabel: (rawData) => `${rawData.name} (${rawData.code})`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanProductIdsFilter,
    getSearchParamKeys: getSearchParamKeysOfProductIdsFilter,
    ...productIdsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findProductsFilterOptions,
    queryKeyFn: getFindProductsFilterOptionsKey,
    defaultValues: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.productIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'codeOrName',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_LIST_FILTERS_KEYS.productIds,
    formatChipLabel: (rawData) =>
      `${rawData.code} ${rawData.referenceCode ? `(${rawData.referenceCode})` : ''} ${rawData.name}`,
    formatLabel: (rawData) =>
      `${rawData.name}\n ${rawData.code} ${rawData.referenceCode ? `(${rawData.referenceCode})` : ''}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanCategoryIdsFilter,
    getSearchParamKeys: getSearchParamKeysOfCategoryIdsFilter,
    ...categoryIdsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCategoriesFilterOptions,
    queryKeyFn: getFindCategoriesFilterOptionsKey,
    defaultValues: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.categoryIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'nameOrCode',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_LIST_FILTERS_KEYS.categoryIds,
    formatLabel: (rawData) => rawData.name,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
    aggregatingOptionParams: {
      aggregatingValueKey: 'groupId',
      aggregatingLabelKey: 'groupName',
    },
  });

  const {
    cleanFilter: cleanDeliveryWindowIdsFilter,
    getSearchParamKeys: getSearchParamKeysOfDeliveryWindowIdsFilter,
    ...deliveryWindowIdsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findDeliveryWindowsFilterOptions,
    queryKeyFn: getFindDeliveryWindowsFilterOptionsKey,
    defaultValues: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.deliveryWindowIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'name',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_LIST_FILTERS_KEYS.deliveryWindowIds,
    formatLabel: (rawData) => `${rawData.name} (${rawData.code})`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanAvailabilityFilter,
    getSearchParamKeys: getSearchParamKeysOfAvailabilityFilter,
    ...availabilityFilter
  } = useMultiStaticOptionsSelector({
    key: CATALOG_LIST_FILTERS_KEYS.availability,
    defaultValues: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.availability],
    shouldIgnoreDefaultFilters,
    staticOptions: skuAvailabilityOptions.map((av) => ({
      ...av,
      label: translate(av.label),
    })),
  });

  const {
    cleanFilter: cleanHasPromotionFilter,
    getSearchParamKeys: getSearchParamKeysOfHasPromotionFilter,
    ...hasPromotionFilter
  } = useRadioFilter({
    key: CATALOG_LIST_FILTERS_KEYS.hasPromotion,
    defaultValue: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.hasPromotion] as string,
    shouldIgnoreDefaultFilters,
    options: [
      { value: 'true', label: translate(LangEnum.GLOBAL_YES) },
      { value: 'false', label: translate(LangEnum.GLOBAL_NO) },
    ],
  });

  const {
    cleanFilter: cleanIsKitFilter,
    getSearchParamKeys: getSearchParamKeysOfIsKitFilter,
    ...isKitFilter
  } = useRadioFilter({
    key: CATALOG_LIST_FILTERS_KEYS.isKit,
    defaultValue: defaultFilters?.[CATALOG_LIST_FILTERS_KEYS.isKit] as string,
    shouldIgnoreDefaultFilters,
    options: [
      { value: 'true', label: translate(LangEnum.GLOBAL_YES) },
      { value: 'false', label: translate(LangEnum.GLOBAL_NO) },
    ],
  });

  const options: FilterOptionV3[] = [
    {
      type: PossibleFilters.RADIO,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_IS_DEFAULT),
      radioArgs: isDefaultFilter,
      cleanFilter: cleanIsDefaultFilter,
      getSearchParamKeys: getSearchParamKeysOfIsDefaultFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_CATALOG,
      toHide: shouldHideAdminOnlyFilters,
    },
    {
      type: PossibleFilters.RADIO,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_IS_DYNAMIC),
      radioArgs: isDynamicFilter,
      cleanFilter: cleanIsDynamicFilter,
      getSearchParamKeys: getSearchParamKeysOfIsDynamicFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_CATALOG,
      toHide: shouldHideAdminOnlyFilters,
    },
    {
      type: PossibleFilters.RADIO,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_IS_PUBLIC_LINK),
      radioArgs: isPublicLinkFilter,
      cleanFilter: cleanIsPublicLinkFilter,
      getSearchParamKeys: getSearchParamKeysOfIsPublicLinkFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_CATALOG,
      toHide: shouldHideAdminOnlyFilters,
    },
    {
      type: PossibleFilters.DATE_RANGE,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_PERIOD),
      dateRangeArgs: dateRangeFilter,
      cleanFilter: cleanDateRangeFilter,
      getSearchParamKeys: getSearchParamKeysOfDateRangeFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_CATALOG,
      toHide: shouldHideAdminOnlyFilters,
    },

    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_CUSTOMERS),
      label: translate(LangEnum.CATALOGS_LIST_FILTER_CUSTOMERS),
      multiDynamicOptionsArgs: customerIdsFilter,
      cleanFilter: cleanCustomerIdsFilter,
      getSearchParamKeys: getSearchParamKeysOfCustomerIdsFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_ACCESS,
      toHide: shouldHideAdminOnlyFilters,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_SALES_REPRESENTATIVES),
      label: translate(LangEnum.CATALOGS_LIST_FILTER_SALES_REPRESENTATIVES),
      multiDynamicOptionsArgs: salesRepresentativeIdsFilter,
      cleanFilter: cleanSalesRepresentativeIdsFilter,
      getSearchParamKeys: getSearchParamKeysOfSalesRepresentativeIdsFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_ACCESS,
      toHide: shouldHideAdminOnlyFilters,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_CLASSIFICATIONS),
      label: translate(LangEnum.CATALOGS_LIST_FILTER_CLASSIFICATIONS),
      multiDynamicOptionsArgs: classificationPermissionIdsFilter,
      cleanFilter: cleanClassificationPermissionIdsFilter,
      getSearchParamKeys: getSearchParamKeysOfClassificationPermissionIdsFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_ACCESS,
      toHide: shouldHideAdminOnlyFilters,
    },

    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_PRODUCTS),
      multiDynamicOptionsArgs: productIdsFilter,
      cleanFilter: cleanProductIdsFilter,
      getSearchParamKeys: getSearchParamKeysOfProductIdsFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_PRODUCT,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_CATEGORY),
      multiDynamicOptionsArgs: categoryIdsFilter,
      cleanFilter: cleanCategoryIdsFilter,
      getSearchParamKeys: getSearchParamKeysOfCategoryIdsFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_PRODUCT,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA,
      title: translate(LangEnum.CATALOGS_FILTER_AVAILABILITY),
      hideSearchField: true,
      multiStaticOptionsArgs: availabilityFilter,
      cleanFilter: cleanAvailabilityFilter,
      getSearchParamKeys: getSearchParamKeysOfAvailabilityFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_PRODUCT,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_PRODUCT_DELIVERY_WINDOWS),
      label: translate(LangEnum.CATALOGS_FILTER_DELIVERY_WINDOW_LABEL),
      multiDynamicOptionsArgs: deliveryWindowIdsFilter,
      cleanFilter: cleanDeliveryWindowIdsFilter,
      getSearchParamKeys: getSearchParamKeysOfDeliveryWindowIdsFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_PRODUCT,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_COLLECTIONS),
      multiDynamicOptionsArgs: collectionIdsFilter,
      cleanFilter: cleanCollectionIdsFilter,
      getSearchParamKeys: getSearchParamKeysOfCollectionIdsFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_PRODUCT,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_COLORS),
      multiDynamicOptionsArgs: colorIdsFilter,
      cleanFilter: cleanColorIdsFilter,
      getSearchParamKeys: getSearchParamKeysOfColorIdsFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_PRODUCT,
    },
    {
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_PRODUCT,
      title: translate(LangEnum.GLOBAL_CLASSIFICATIONS),
      type: PossibleFilters.OPTIONS_SELECTOR_CREATOR,
      getLabel: (values) => translate(LangEnum.CATALOGS_FILTER_CLASSIFICATION_TITLE, values),
      getSearchParamKeys: () => {
        const valuesKey = Object.keys(searchParams);
        const keys = valuesKey.filter((key) => key.startsWith(CLASSIFICATIONS_PREFIX));
        return keys;
      },
      cleanFilter: () => {
        clearFiltersCreatedByCreator();
      },
      multiDynamicOptionsCreatorArgs: {
        filtersQueryFn: classificationTypeRepository.findClassificationTypes,
        filtersQueryKey: getFindClassificationTypesKey,
        filtersQueryParams: {
          limit: 100,
          skip: 0,
          useAsFilter: true,
          entity: ClassificationTypeEntityEnum.PRODUCT_COLOR,
          'order[exhibition_order]': 'ASC',
        },
        dynamicOptionQueryFn: repository.findClassificationsFilterOptions,
        dynamicOptionQueryKey: getFindClassificationsFilterOptionsKey,
        keys: {
          filterId: 'id',
          filterTitle: 'name',
          idsPrefix: CLASSIFICATIONS_PREFIX,
          keyToRequestOptions: 'classificationTypeId',
        },
        hookArgs: {
          paramKeyToSearchMore: 'query',
          paramKeyToSearchSpecifics: 'ids',
          shouldIgnoreDefaultFilters,
          defaultValues: defaultFiltersByPrefix?.[CLASSIFICATIONS_PREFIX],
          formatLabel: (rawData) => `${rawData.name} (${rawData.code})`,
          defaultQueryParam: {
            limit: queryLimit,
          },
          order: {
            name: 'ASC',
          },
        },
      },
    } as MultiDynamicOptionsCreatorFilter<
      Parameters<typeof classificationTypeRepository.findClassificationTypes>[0],
      Awaited<ReturnType<typeof classificationTypeRepository.findClassificationTypes>>,
      Parameters<typeof repository.findClassificationsFilterOptions>[0],
      Awaited<ReturnType<typeof repository.findClassificationsFilterOptions>>
    >,
    {
      type: PossibleFilters.RADIO,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_HAS_PROMOTION),
      radioArgs: hasPromotionFilter,
      cleanFilter: cleanHasPromotionFilter,
      getSearchParamKeys: getSearchParamKeysOfHasPromotionFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_PRODUCT,
    },
    {
      type: PossibleFilters.RADIO,
      title: translate(LangEnum.CATALOGS_LIST_FILTER_IS_KIT),
      radioArgs: isKitFilter,
      cleanFilter: cleanIsKitFilter,
      getSearchParamKeys: getSearchParamKeysOfIsKitFilter,
      groupBy: LangEnum.CATALOGS_LIST_FILTER_SECTION_PRODUCT,
    },
  ];

  return {
    options,
    preferenceList: list,
    columnsVisibility: null,
    searchField,
    isSearchActive,
    onClearSearch,
  };
};

export default useCatalogListFilter;
